import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PaginationState } from 'app/shared/pagination/pagination.model';
import { Loader } from 'app/shared/loader/loader';
import { Alert, Button, Col, Row, Table } from 'reactstrap';
import { Pagination } from 'edozvola-frontend-lib/dist/ui';
import { GenericTableViewFormProps } from 'app/shared/generic-table-view-form/generic-table-view-form.model';
import { renderPaginationFooter } from 'app/shared/pagination/pagination.util';
import { renderSortingIcons, sortByColumnWithoutUpdateUtil } from 'app/shared/util/table-util';
import { ConfirmationModal } from 'app/shared/modal/confirmation-modal';
import { useQuery } from '@tanstack/react-query';

const GenericTableViewForm = <T extends unknown>({
  title,
  fetchData,
  data,
  renderRow,
  columns,
  pagination,
  setPagination,
  addNewLink,
  addNewButtonName,
  modalProps,
}: GenericTableViewFormProps<T>) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: fetchedData, isLoading } = useQuery({
    queryKey: ['generic-table', { title: title }, { ...pagination }],
    queryFn: ({ signal }) => fetchData && fetchData(id, pagination),
    enabled: !data,
  });

  const content = data || fetchedData?.content;
  const totalElements = content?.length;

  /* const allQueries = queryClient.getQueryCache().findAll();*/

  const onPageChange = (page: number) => {
    if (pagination.page !== page) {
      setPagination((prev: PaginationState) => ({ ...prev, page }));
    }
  };

  const onSizeChange = (size: number) => {
    if (pagination.size !== size) {
      setPagination((prev: PaginationState) => ({ ...prev, size }));
    }
  };

  const sortByColumn = (event: React.MouseEvent<HTMLDivElement>) => sortByColumnWithoutUpdateUtil(event, pagination, setPagination);

  return (
    <div className="container-fluid">
      <h4>{title}</h4>
      {content === undefined ? (
        <Loader />
      ) : (
        <>
          <Row>
            <Col xs={12} className={'d-flex justify-content-end pb-4'}>
              <Button color={'info'} prependIcon={'save'} onClick={() => navigate(addNewLink)}>
                {addNewButtonName}
              </Button>
            </Col>
          </Row>
          {isLoading ? (
            <Loader />
          ) : content?.length === 0 ? (
            <Alert color="info" className="uniform-width">
              Nema podataka.
            </Alert>
          ) : (
            <>
              {modalProps && (
                <ConfirmationModal
                  modalOpened={modalProps.showModal}
                  header={`Brisanje ${title}`}
                  bodyMessage={`Jeste li sigurni da želite obrisati ${title.toLowerCase()}?`}
                  onConfirm={modalProps.onConfirmDelete}
                  onCancel={modalProps.onCancel}
                />
              )}
              <Table size="sm" bordered striped responsive>
                <thead>
                  <tr>
                    {columns.map(col => (
                      <th key={col.field as string}>
                        {col.header} {renderSortingIcons(col.field as string, pagination, sortByColumn)}
                      </th>
                    ))}
                    <th></th>
                  </tr>
                </thead>
                <tbody>{content?.map((value, index) => renderRow(value, index))}</tbody>
              </Table>
              <div className="row">
                <div className="col-lg-6 d-flex justify-content-start">
                  {totalElements && totalElements > 10 && (
                    <Pagination
                      page={pagination.page}
                      size={pagination.size}
                      total={totalElements}
                      onChange={onPageChange}
                      onSizeChange={onSizeChange}
                      sizeOptions={[10, 25, 50]}
                      className="float-left"
                    />
                  )}
                </div>
                <div className="col-lg-6 text-end">
                  <h6>{renderPaginationFooter({ ...pagination, total: totalElements })}</h6>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default GenericTableViewForm;
